import { useEffect, useState, useRef, useCallback } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import styled from "styled-components";
import { Flex, Image, Text } from "rebass/styled-components";
import { FaPlay } from "react-icons/fa";

import { useWatchLimit } from "MVVM/Hooks/useWatchLimit";
import {
  PlaylistClipsState,
  ActivePlaylistClipState,
  NextUpPlaylistState,
  WatchedClipsCountState,
} from "MVVM/State/embeddablePlaylist";
import { ActiveOverlayPopUpState, PlatformState } from "MVVM/State/iframe";
import { PlaylistClip } from "MVVM/Views/EmbeddablePlaylist/@types";
import { formatPlaylistClip } from "MVVM/Views/EmbeddablePlaylist/Utilities";
import { CountdownCircle } from "MVVM/Views/EmbeddablePlaylist/Components/CountdownCircle";
import "./index.css";

export const NextUpPopUp = ({ clip }: any) => {
  const [countdown, setCountdown] = useState(6);
  const [nextClip, setNextClip] = useState<PlaylistClip | null>(null);
  const [countdownActive, setCountdownActive] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playlistClips = useRecoilValue(PlaylistClipsState);
  const setActivePlaylistClip = useSetRecoilState(ActivePlaylistClipState);
  const [activeOverlayPopUp, setActiveOverlayPopUp] = useRecoilState(
    ActiveOverlayPopUpState,
  );
  const isNextUpPlaylist = useRecoilValue(NextUpPlaylistState);
  const platform = useRecoilValue(PlatformState);

  const [watchedClipsCount, setWatchedClipsCount] = useRecoilState(
    WatchedClipsCountState,
  );

  const { handleWatchLimitReached } = useWatchLimit();

  useEffect(() => {
    if (
      activeOverlayPopUp === "nextUp" ||
      activeOverlayPopUp === "nextUpPlaylist"
    ) {
      setCountdown(6);
      setCountdownActive(true);
    }
  }, [clip?._id, activeOverlayPopUp]);

  useEffect(() => {
    if (!playlistClips) return undefined;
    const index = playlistClips.findIndex(
      (playlistClip) => playlistClip._id === clip?._id,
    );
    if (index >= 0 && index < playlistClips.length - 1) {
      setNextClip(playlistClips[index + 1]);
    } else {
      setNextClip(null);
    }
  }, [playlistClips, clip._id]);

  const updateClip = useCallback(() => {
    if (nextClip) {
      if (watchedClipsCount >= 2) {
        setCountdownActive(false);
        return handleWatchLimitReached(clip.username || "", nextClip);
      }

      const formattedClip = formatPlaylistClip(nextClip);
      setActivePlaylistClip(formattedClip);
      setActiveOverlayPopUp(null);
      setWatchedClipsCount((prev) => prev + 1);
    } else {
      if (!playlistClips) return;
      const formattedClip = formatPlaylistClip(playlistClips[0]);
      setActivePlaylistClip(formattedClip);
      setActiveOverlayPopUp(null);
    }
  }, [
    nextClip,
    setActivePlaylistClip,
    setActiveOverlayPopUp,
    setWatchedClipsCount,
    handleWatchLimitReached,
    watchedClipsCount,
    clip.username,
    playlistClips,
  ]);

  useEffect(() => {
    if (!countdownActive) return;
    if (
      activeOverlayPopUp !== "nextUp" &&
      activeOverlayPopUp !== "nextUpPlaylist"
    )
      return;
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          updateClip();
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [
    nextClip,
    setActivePlaylistClip,
    setActiveOverlayPopUp,
    activeOverlayPopUp,
    updateClip,
    countdownActive,
  ]);

  if (
    activeOverlayPopUp !== "nextUp" &&
    activeOverlayPopUp !== "nextUpPlaylist"
  )
    return null;

  return (
    <StyledContainer $isNextUpPlaylist={isNextUpPlaylist}>
      <StyledFixedWidth className="next-up-fixed-width">
        {isNextUpPlaylist && (
          <Flex
            style={{ position: "relative", cursor: "pointer" }}
            onClick={updateClip}
          >
            <StyledThumbnailImage
              className="next-up-image"
              src={nextClip?.thumbnailStandardUrl}
            />
            <PlayIcon className="next-up-play-icon" />
          </Flex>
        )}
        <StyledTextContainer $isNextUpPlaylist={isNextUpPlaylist}>
          <Flex flexDirection="column">
            <StyledNextUpText className="next-up-text">
              Next Up:
            </StyledNextUpText>
            <StyledTitleText className="next-up-title">
              {nextClip?.title}
            </StyledTitleText>
          </Flex>
          <CountdownCircle countdown={countdown} max={6} size={24} />
        </StyledTextContainer>
      </StyledFixedWidth>
    </StyledContainer>
  );
};

const StyledContainer = styled(Flex)<{ $isNextUpPlaylist: boolean }>`
  align-items: center;
  background: rgb(0, 0, 0, 0.8);
  border-radius: ${({ $isNextUpPlaylist }) =>
    $isNextUpPlaylist ? "0" : "12px"};
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const StyledThumbnailImage = styled(Image)`
  height: 90px;
  width: 160px;
`;

const PlayIcon = styled(FaPlay)`
  height: 35%;
  left: 56.25%;
  position: absolute;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 35%;
`;

const StyledNextUpText = styled(Text)`
  color: ${({ theme }) => theme.colors.iceAlpha60};
  font-size: 8px;
  font-weight: 500;
  line-height: 10px;
`;

const StyledTitleText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 8px;
  font-weight: 700;
  line-height: 10px;
`;

const StyledFixedWidth = styled(Flex)`
  flex-direction: column;
  pointer-events: auto;
  width: 160px;
`;

const StyledTextContainer = styled(Flex)<{ $isNextUpPlaylist: boolean }>`
  justify-content: space-between;
  margin-top: ${({ $isNextUpPlaylist }) => ($isNextUpPlaylist ? "10px" : "0")};
`;
